import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressRolesManagementApiSiteRolesManagementServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/contributors/accept',
        destPath: '/contributors/accept',
      },
      {
        srcPath: '/roles-management-web',
        destPath: '',
      },
      {
        srcPath: '/team/accept',
        destPath: '/team/accept',
      },
      {
        srcPath: '/_api/roles-management-web',
        destPath: '',
      },
      {
        srcPath: '/app/accept',
        destPath: '/app/accept',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/roles-management-web',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/roles-management-web',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/roles-management-web',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/roles-management-web',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/roles-management-web',
        destPath: '',
      },
      {
        srcPath: '/roles-management-web',
        destPath: '',
      },
      {
        srcPath: '/contributors/accept',
        destPath: '/contributors/accept',
      },
      {
        srcPath: '/team/accept',
        destPath: '/team/accept',
      },
    ],
    'dev._base_domain_': [
      {
        srcPath: '/_api/roles-management-web',
        destPath: '',
      },
    ],
    'platform.rise.ai': [
      {
        srcPath: '/v2/contributors',
        destPath: '/v2/contributors',
      },
      {
        srcPath: '/contributor/change/role',
        destPath: '/contributor/change/role',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/roles-management',
        destPath: '',
      },
      {
        srcPath: '/roles-management-web',
        destPath: '',
      },
      {
        srcPath: '/roles-management/roles',
        destPath: '/roles',
      },
      {
        srcPath: '/roles-management/team',
        destPath: '/team',
      },
      {
        srcPath: '/roles-management/v3/team',
        destPath: '/v3/team',
      },
      {
        srcPath: '/roles-management/v2/team',
        destPath: '/v2/team',
      },
      {
        srcPath: '/roles-management/contributor/change',
        destPath: '/contributor/change',
      },
      {
        srcPath: '/roles-management/v2/contributors',
        destPath: '/v2/contributors',
      },
      {
        srcPath: '/roles-management/contributor/remove',
        destPath: '/contributor/remove',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/identity/contributor',
        destPath: '/contributor',
      },
    ],
    'vibe._base_domain_': [
      {
        srcPath: '/roles-management-web/v2/contributors',
        destPath: '/v2/contributors',
      },
      {
        srcPath: '/roles-management-web/myRoles',
        destPath: '/roles-management-web/myRoles',
      },
    ],
    _: [
      {
        srcPath: '/identity/contributor',
        destPath: '/contributor',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_user-management_contributors';

/**
 * returns a list of all the contributors + invites to be contributors of a given meta site ID taken from the
 * context.
 */
export function getSiteContributorsV2(
  payload: object
): RequestOptionsFactory<any> {
  function __getSiteContributorsV2({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fields' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.identity.v1.contributor',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.roles.management.api.SiteRolesManagementService.GetSiteContributorsV2',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressRolesManagementApiSiteRolesManagementServiceUrl({
        protoPath: '/v2/contributors',
        data: serializedData,
        host,
      }),
      params: toURLSearchParams(serializedData),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'contributors.joinedAt' },
              { path: 'invites.dateCreated' },
              { path: 'invites.expirationDate' },
              { path: 'pendingOwner.expirationDate' },
              { path: 'enrichedInvites.dateCreated' },
              { path: 'enrichedInvites.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getSiteContributorsV2;
}

/**
 * Updates all of a contributor's roles for the specified site, overriding any existing roles.
 *
 * Replaces all existing role assignments for the contributor with the roles specified in `newRoles`. To retrieve available roles, call [Get Roles Info](https://dev.wix.com/docs/api-reference/account-level/user-management/accounts/users/get-roles-info).
 */
export function changeRole(payload: object): RequestOptionsFactory<any> {
  function __changeRole({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.identity.v1.contributor',
      method: 'PUT' as any,
      methodFqn:
        'com.wixpress.roles.management.api.SiteRolesManagementService.ChangeRole',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressRolesManagementApiSiteRolesManagementServiceUrl({
        protoPath: '/contributor/change/role',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __changeRole;
}

/**
 * Updates the business locations within all a contributor's role assignments for the specified site, overriding any existing locations.
 *
 * Replaces all existing location assignments for the contributor's roles with the locations specified in `newLocations`. Get location IDs from the [Locations API](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).
 */
export function changeContributorLocation(
  payload: object
): RequestOptionsFactory<any> {
  function __changeContributorLocation({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.identity.v1.contributor',
      method: 'PUT' as any,
      methodFqn:
        'com.wixpress.roles.management.api.SiteRolesManagementService.ChangeContributorLocation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressRolesManagementApiSiteRolesManagementServiceUrl({
        protoPath: '/contributor/change/locations',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __changeContributorLocation;
}

/**
 * Retrieves a list of contributors for the specified site, given the provided filters.
 *
 * Returns contributor account IDs and account owner IDs that match the specified filters.
 */
export function querySiteContributors(
  payload: object
): RequestOptionsFactory<any> {
  function __querySiteContributors({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.identity.v1.contributor',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.roles.management.api.SiteRolesManagementService.QuerySiteContributors',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressRolesManagementApiSiteRolesManagementServiceUrl({
        protoPath: '/v2/contributors/query',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __querySiteContributors;
}

/** returns the quota information for contributors on the given site. */
export function getContributorsQuota(
  payload: object
): RequestOptionsFactory<any> {
  function __getContributorsQuota({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.identity.v1.contributor',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.roles.management.api.SiteRolesManagementService.GetContributorsQuota',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressRolesManagementApiSiteRolesManagementServiceUrl({
        protoPath: '/v2/contributors/quota',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getContributorsQuota;
}

/** Removes a contributor from the site by clearing all their role assignments. */
export function removeContributor(payload: object): RequestOptionsFactory<any> {
  function __removeContributor({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.identity.v1.contributor',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.roles.management.api.SiteRolesManagementService.RemoveContributor',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressRolesManagementApiSiteRolesManagementServiceUrl({
        protoPath: '/contributor/remove',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __removeContributor;
}
